// Constants
import { MyTasksViewPivotType } from "@ms/uno-constants/lib/local/ViewPivots";
// Stores
import { DefaultPlanSource } from "@ms/planner-models/lib/local/client/planner/DefaultPlanSource";
// Utilities
import { Temporal } from "temporal-polyfill-lite";
/** Stable grouping key that coalesces all private-task backing plans into one user-facing group. */ export const PrivateTasksVirtualGroupKey = "privateTasks";
/** Returns the virtual Private Tasks key for either default-plan source, otherwise the canonical plan ID. */ export function getMyTasksPlanGroupKey(planId, planStore) {
    return planStore.isDefaultPlan(planId) ? PrivateTasksVirtualGroupKey : planId;
}
/**
 * Merge assigned-to-me tasks with private (default plan) tasks, deduplicating by task ID, and flagged email tasks.
 * Shared by Important, All, and DueSoon pivots which all need the combined task set.
 */ export function getMergedAssignedAndPrivateTasks(currentUserId, taskStore, planStore) {
    const assignedTasks = taskStore.getTasksAssignedToUser(currentUserId).data ?? [];
    const flaggedEmailPlanId = planStore.getFlaggedEmailPlanId();
    const privateTasks = taskStore.getPrivateTasks().data ?? [];
    const flaggedEmailTasks = flaggedEmailPlanId ? taskStore.getTasksForPlan(flaggedEmailPlanId).data ?? [] : [];
    const seenIds = new Set();
    const merged = [];
    for (const task of assignedTasks){
        if (!seenIds.has(task.id)) {
            seenIds.add(task.id);
            merged.push(task);
        }
    }
    for (const task of privateTasks){
        if (!seenIds.has(task.id)) {
            seenIds.add(task.id);
            merged.push(task);
        }
    }
    for (const task of flaggedEmailTasks){
        if (!seenIds.has(task.id)) {
            seenIds.add(task.id);
            merged.push(task);
        }
    }
    return merged;
}
/**
 * Get filtered tasks for a MyTasks pivot type.
 * This is a pure selector that reads from stores and applies the appropriate filter.
 * @param pivotType The active MyTasks pivot
 * @param currentUserId The current user's ID
 * @param stores Stores to read task/plan/mru data from
 */ export function getFilteredTasksForPivot(pivotType, currentUserId, stores) {
    const { taskStore, planStore, pinnedTasksStore, isStarredTaskPropertyEnabled } = stores;
    switch(pivotType){
        case MyTasksViewPivotType.PrivateTasks:
            {
                return taskStore.getPrivateTasks().data ?? [];
            }
        case MyTasksViewPivotType.FlaggedEmails:
            {
                const flaggedEmailPlanId = planStore.getFlaggedEmailPlanId();
                if (!flaggedEmailPlanId) {
                    return [];
                }
                return taskStore.getTasksForPlan(flaggedEmailPlanId).data ?? [];
            }
        case MyTasksViewPivotType.ThisWeek:
            {
                const allTasks = getMergedAssignedAndPrivateTasks(currentUserId, taskStore, planStore);
                const today = Temporal.Now.plainDateISO();
                const dayOfWeek = today.dayOfWeek; // 1=Monday … 7=Sunday
                const startOfWeek = today.subtract({
                    days: dayOfWeek - 1
                });
                const endOfWeek = startOfWeek.add({
                    days: 6
                });
                return allTasks.filter((task)=>{
                    if (!task.dueDateTime) {
                        return false;
                    }
                    const dueDate = task.dueDateTime.date;
                    return Temporal.PlainDate.compare(dueDate, startOfWeek) >= 0 && Temporal.PlainDate.compare(dueDate, endOfWeek) <= 0;
                });
            }
        case MyTasksViewPivotType.Important:
            {
                const allTasks = getMergedAssignedAndPrivateTasks(currentUserId, taskStore, planStore);
                return allTasks.filter((task)=>isStarredTaskPropertyEnabled && task.isStarred || pinnedTasksStore.isTaskPinned(task.id));
            }
        case MyTasksViewPivotType.All:
        case MyTasksViewPivotType.DueSoon:
            {
                return getMergedAssignedAndPrivateTasks(currentUserId, taskStore, planStore);
            }
        case MyTasksViewPivotType.AssignedToMe:
        default:
            {
                return taskStore.getTasksAssignedToUser(currentUserId).data ?? [];
            }
    }
}
/**
 * Group tasks by their plan and sort favorited tasks to the top within each group.
 * @param tasks The list of tasks to group
 * @param planStore Plan store for looking up plan objects
 * @param pinnedTaskIds Set of task IDs that are pinned/favorited
 */ export function groupTasksByPlanSortedByFavorite(tasks, planStore, pinnedTaskIds) {
    const groupMap = new Map();
    for (const task of tasks){
        const groupKey = getMyTasksPlanGroupKey(task.planId, planStore);
        const existing = groupMap.get(groupKey);
        if (existing) {
            existing.push(task);
        } else {
            groupMap.set(groupKey, [
                task
            ]);
        }
    }
    const groups = [];
    for (const [groupKey, planTasks] of groupMap){
        const planId = groupKey === PrivateTasksVirtualGroupKey ? planStore.getDefaultPlanId(DefaultPlanSource.Planner) ?? planStore.getDefaultPlanId(DefaultPlanSource.ToDo) : groupKey;
        if (!planId) {
            continue;
        }
        const plan = planStore.getPlan(planId).data;
        if (plan) {
            const sorted = [
                ...planTasks
            ].sort((a, b)=>{
                const aFav = pinnedTaskIds.has(a.id) ? 0 : 1;
                const bFav = pinnedTaskIds.has(b.id) ? 0 : 1;
                return aFav - bFav;
            });
            groups.push({
                plan,
                tasks: sorted
            });
        }
    }
    return groups;
}
